import React, { useEffect, useState, useCallback, useContext } from "react";
import axios from "axios";
import {
	Grid,
	Card,
	CardContent,
	Typography,
	Button,
	CircularProgress,
	TextField,
	Box,
} from "@mui/material";
import { motion } from "framer-motion";
import { FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const Logs = () => {
	const navigate = useNavigate();
	const { logout } = useContext(AuthContext);

	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [batchIdFilter, setBatchIdFilter] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	const getFiles = useCallback(async () => {
		setLoading(true);
		const token = localStorage.getItem("token");

		if (!token) {
			logout();
			toast.error("No authentication token found. Please log in.");
			setLoading(false);
			return;
		}

		try {
			const { data, status } = await axios.get(
				"https://parallex.approot.ng/list.php",
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (status === 401 || status === 403) {
				toast.error(
					data?.message || "Authentication failed. Please log in again."
				);
				logout();
				setLoading(false);
				return;
			}

			let fileList = [];
			if (Array.isArray(data)) {
				fileList = data;
			} else if (data && data.status && Array.isArray(data.data)) {
				fileList = data.data;
			}

			// Parse filenames into structured data
			const parsed = fileList.map((f) => {
				const match = f.match(/^report_(\d+)_(\d{4}-\d{2}-\d{2})\.(csv|zip)$/);
				if (match) {
					return {
						name: f,
						batchId: match[1],
						date: match[2],
					};
				}
				return { name: f, batchId: "", date: "" };
			});

			setFiles(parsed);
			setLoading(false);
		} catch (e) {
			console.error("Error fetching file list:", e);
			toast.error("An error occurred while fetching files.");
			setLoading(false);

			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status === 401 || e.response.status === 403) {
					logout();
					toast.info("Authentication failed. Please log in again.");
				}
			}
		}
	}, [logout]);

	useEffect(() => {
		getFiles();
	}, [getFiles]);

	const handleDownload = (filename) => {
		const url = `https://parallex.approot.ng/downloadcsv2.php?file=${filename}`;
		window.open(url, "_blank");
	};

	// Download all files in date range one by one
	// const handleDownloadMonth = async () => {
	// 	if (!startDate || !endDate) {
	// 		toast.warning("Please select a start and end date.");
	// 		return;
	// 	}

	// 	const filesToDownload = files.filter((file) => {
	// 		const fileDate = new Date(file.date);
	// 		return (
	// 			fileDate >= new Date(startDate) && fileDate <= new Date(endDate)
	// 		);
	// 	});

	// 	if (filesToDownload.length === 0) {
	// 		toast.info("No files found in the selected date range.");
	// 		return;
	// 	}

	// 	toast.info(`Starting download of ${filesToDownload.length} files...`);

	// 	for (let i = 0; i < filesToDownload.length; i++) {
	// 		const file = filesToDownload[i];
	// 		handleDownload(file.name);
	// 		await new Promise((resolve) => setTimeout(resolve, 1500)); // wait 1.5s between downloads
	// 	}

	// 	toast.success("All downloads initiated successfully.");
	// };

	// Remove the problematic loop and delay
	const handleDownloadMonth = async () => {
		if (!startDate || !endDate) {
			toast.warning("Please select a start and end date.");
			return;
		}

		// 1. Construct the single batch download URL
		const url = `https://parallex.approot.ng/downloadcsv.php?start_date=${startDate}&end_date=${endDate}`;

		toast.info(
			`Attempting to generate and download reports from ${startDate} to ${endDate}.`
		);

		// 2. Use window.open to trigger the single ZIP download
		// The browser will allow this single call.
		window.open(url, "_blank");

		// Note: Since this is a direct browser download, we can't reliably check
		// for success/failure on the frontend without a more complex method
		// (like using an iframe). We assume it was successful or the browser
		// will show an error.

		// You can remove the success toast or change it:
		// toast.success("Batch download request initiated. Check your downloads!");
	};

	// Apply filters
	const filteredFiles = files.filter((file) => {
		const matchBatch = batchIdFilter
			? file.batchId.includes(batchIdFilter)
			: true;
		const fileDate = new Date(file.date);
		const matchDateRange =
			startDate && endDate
				? fileDate >= new Date(startDate) && fileDate <= new Date(endDate)
				: true;

		return matchBatch && matchDateRange;
	});

	if (loading) {
		return (
			<div className='flex justify-center items-center min-h-[200px] font-inter'>
				<CircularProgress />
			</div>
		);
	}

	return (
		<div className='font-inter p-6'>
			<Typography
				variant='h4'
				gutterBottom
				align='center'
				sx={{ mb: 4, fontWeight: "bold", color: "#1E293B" }}
			>
				📂 Logs & Reports
			</Typography>

			{/* Filter Bar */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					gap: 3,
					mb: 6,
					p: 3,
					borderRadius: "16px",
					backdropFilter: "blur(12px)",
					background: "rgba(255,255,255,0.6)",
					boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
					flexWrap: "wrap",
				}}
			>
				<TextField
					label='🔍 Batch ID'
					variant='outlined'
					size='small'
					value={batchIdFilter}
					onChange={(e) => setBatchIdFilter(e.target.value)}
					sx={{ minWidth: "180px" }}
				/>
				<TextField
					label='📅 Start Date'
					type='date'
					variant='outlined'
					size='small'
					value={startDate}
					onChange={(e) => setStartDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					sx={{ minWidth: "180px" }}
				/>
				<TextField
					label='📅 End Date'
					type='date'
					variant='outlined'
					size='small'
					value={endDate}
					onChange={(e) => setEndDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
					sx={{ minWidth: "180px" }}
				/>
				<Button
					variant='contained'
					startIcon={<FaDownload />}
					onClick={handleDownloadMonth}
					sx={{
						background: "linear-gradient(90deg, #D54B01, #FF7E33)",
						"&:hover": {
							background: "linear-gradient(90deg, #b64000, #e66a00)",
						},
						textTransform: "none",
						borderRadius: "12px",
						px: 3,
						py: 1.2,
						fontWeight: "bold",
						boxShadow: "0 4px 12px rgba(213,75,1,0.3)",
					}}
				>
					Download Files in Range
				</Button>
			</Box>

			{/* File Grid */}
			<Grid container spacing={4} justifyContent='center'>
				{filteredFiles.length > 0 ? (
					filteredFiles.map((file, index) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={index}>
							<motion.div
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.98 }}
								transition={{ type: "spring", stiffness: 200, damping: 10 }}
							>
								<Card
									variant='outlined'
									sx={{
										borderRadius: "16px",
										boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
										background: "linear-gradient(135deg, #f9fafb, #f1f5f9)",
									}}
								>
									<CardContent
										sx={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											p: 3,
										}}
									>
										<Typography
											variant='body1'
											gutterBottom
											sx={{
												mb: 2,
												textAlign: "center",
												wordBreak: "break-word",
												fontWeight: 500,
												color: "#334155",
											}}
										>
											{file.name}
										</Typography>
										<Button
											variant='contained'
											startIcon={<FaDownload />}
											onClick={(e) => {
												e.stopPropagation();
												handleDownload(file.name);
											}}
											sx={{
												background: "linear-gradient(90deg, #D54B01, #FF7E33)",
												"&:hover": {
													background:
														"linear-gradient(90deg, #b64000, #e66a00)",
												},
												textTransform: "none",
												borderRadius: "12px",
												px: 3,
												py: 1.2,
												fontWeight: "bold",
												boxShadow: "0 4px 12px rgba(213,75,1,0.3)",
											}}
											fullWidth
										>
											Download
										</Button>
									</CardContent>
								</Card>
							</motion.div>
						</Grid>
					))
				) : (
					<Grid item xs={12}>
						<Typography
							variant='body1'
							color='textSecondary'
							align='center'
							sx={{ mt: 6 }}
						>
							No files available.
						</Typography>
					</Grid>
				)}
			</Grid>
		</div>
	);
};

export default Logs;
